<template>
  <div v-if="view.state !== 'hidden'" ref="block" class="white-balance">
    <template v-if="view.videoUrl">
      <label>White Balance</label>
      <Admin_Tests_VideoViewer :key="view.videoUrl" :options="playerOptions" />
    </template>
    <div
      v-else-if="view.card"
      class="white-balance-card"
      :class="'white-balance-card-' + view.card"
    >
      <span class="white-balance-icon" aria-hidden="true">
        <span
          v-if="view.card === 'preparing'"
          class="white-balance-spinner"
        ></span>
        <svg
          v-else-if="view.card === 'failed'"
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
          />
          <path d="M12 9v4M12 17h.01" />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="8.5" r="5.5" />
          <circle cx="8.5" cy="14.5" r="5.5" />
          <circle cx="15.5" cy="14.5" r="5.5" />
        </svg>
      </span>
      <div class="white-balance-body">
        <div class="white-balance-title">{{ view.title }}</div>
        <div v-if="view.text" class="white-balance-muted">{{ view.text }}</div>
      </div>
      <button
        v-if="view.button"
        type="button"
        class="white-balance-button"
        :disabled="view.button.disabled"
        @click="requestConversion"
      >
        {{ view.button.label }}
      </button>
      <span
        v-if="view.card === 'preparing'"
        class="white-balance-bar"
        aria-hidden="true"
      >
        <span class="white-balance-bar-segment"></span>
      </span>
    </div>
    <p
      v-else-if="view.state === 'notEligible'"
      class="white-balance-line white-balance-muted"
    >
      <svg
        class="white-balance-line-icon"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
      <span v-if="view.text">{{ view.text }}</span>
    </p>
    <p v-else-if="view.text" class="white-balance-line white-balance-muted">
      {{ view.text }}
    </p>
    <p v-if="view.unavailableText" class="white-balance-line white-balance-muted">
      {{ view.unavailableText }}
    </p>
    <p v-if="requestMessage" class="white-balance-line white-balance-error">
      {{ requestMessage }}
    </p>
  </div>
  <div
    v-if="notice"
    class="white-balance-notice"
    :class="'white-balance-notice-' + notice.kind"
    role="status"
  >
    <span class="white-balance-icon" aria-hidden="true">
      <svg
        v-if="notice.kind === 'failed'"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
        />
        <path d="M12 9v4M12 17h.01" />
      </svg>
      <svg
        v-else
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
    </span>
    <div class="white-balance-body">
      <div class="white-balance-title">{{ notice.title }}</div>
      <div class="white-balance-muted">{{ notice.text }}</div>
    </div>
    <button type="button" class="white-balance-button" @click="showBlock">
      Show
    </button>
    <button
      type="button"
      class="white-balance-notice-close"
      aria-label="Close"
      @click="closeNotice"
    >
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        aria-hidden="true"
      >
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    </button>
  </div>
</template>

<script>
import Admin_Tests_VideoViewer from "@/components/Admin.Tests.VideoViewer";
import {
  readFailureHidesBlock,
  resolveNotice,
  resolveRequestFailure,
  resolveWhiteBalanceView,
} from "@/utils/whiteBalanceState";

export default {
  name: "Admin_Tests_WhiteBalance",
  components: {
    Admin_Tests_VideoViewer,
  },
  props: {
    testId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      body: null,
      receivedAtMs: null,
      notFound: false,
      readFailed: false,
      requesting: false,
      requestMessage: null,
      readInFlight: false,
      notice: null,
    };
  },
  computed: {
    view() {
      return resolveWhiteBalanceView(
        {
          body: this.body,
          notFound: this.notFound,
          readFailed: this.readFailed,
          requesting: this.requesting,
        },
        this.receivedAtMs
      );
    },
    // The viewer reads its options once, when it mounts, so it is only
    // mounted once the URL is known. The rest matches the Original and
    // Detected players above so the three videos compare like for like.
    playerOptions() {
      return {
        height: "280",
        autoplay: false,
        muted: true,
        controls: true,
        language: "en",
        playbackRates: [0.5, 1.0, 1.5, 2.0],
        sources: [{ type: "video/mp4", src: this.view.videoUrl }],
      };
    },
  },
  created() {
    // Every read and request takes a number when it starts. An answer older
    // than the one on screen is dropped: a status read that started before a
    // request was answered would otherwise put the earlier state back.
    this.started = 0;
    this.shown = 0;
    this.pollTimer = null;
    this.reading = null;
    this.unmounted = false;
    // Until the observer first reports, and in a browser without one, the
    // block counts as off screen: a notice the reviewer did not need costs a
    // click, a missed one costs them the moment the video is ready.
    this.blockVisible = false;
    this.observer = null;
  },
  mounted() {
    if (typeof IntersectionObserver !== "undefined" && this.$refs.block) {
      this.observer = new IntersectionObserver((entries) => {
        this.blockVisible = entries[entries.length - 1].isIntersecting;
      });
      this.observer.observe(this.$refs.block);
    }
    this.readStatus();
  },
  beforeUnmount() {
    this.unmounted = true;
    this.clearPoll();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    isNewest(ticket) {
      if (ticket < this.shown) return false;
      this.shown = ticket;
      return true;
    },
    show(body) {
      const notice = resolveNotice(this.body, body);
      if (notice && !this.blockVisible) this.notice = notice;
      this.body = body;
      // The elapsed time is worked out from when the answer arrived and is
      // refreshed only by the next answer, so no clock runs in the block.
      this.receivedAtMs = Date.now();
      this.readFailed = false;
    },
    showBlock() {
      this.notice = null;
      if (this.$refs.block) this.$refs.block.scrollIntoView({ block: "center" });
    },
    closeNotice() {
      this.notice = null;
    },
    clearPoll() {
      if (this.pollTimer !== null) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    schedulePoll() {
      this.clearPoll();
      const delay = this.view.pollDelayMs;
      if (this.unmounted || this.readInFlight || delay === null) return;
      this.pollTimer = setTimeout(() => {
        this.pollTimer = null;
        this.readStatus();
      }, delay);
    },
    // Only one read runs at a time.
    readStatus() {
      if (this.unmounted) return Promise.resolve();
      if (this.readInFlight) return this.reading;
      this.clearPoll();
      this.readInFlight = true;
      const ticket = ++this.started;
      this.reading = this.$store
        .dispatch("Admin/GetWhiteBalanceStatus", this.testId)
        .then((body) => {
          if (this.isNewest(ticket)) this.show(body);
        })
        .catch((err) => {
          if (!this.isNewest(ticket)) return;
          if (readFailureHidesBlock(err)) {
            this.notFound = true;
          } else {
            this.readFailed = true;
          }
        })
        .then(() => {
          this.readInFlight = false;
          this.reading = null;
          this.schedulePoll();
        });
      return this.reading;
    },
    requestConversion() {
      if (this.requesting) return;
      this.requesting = true;
      this.requestMessage = null;
      // An open notice reports on the attempt this request replaces.
      this.notice = null;
      const ticket = ++this.started;
      this.$store
        .dispatch("Admin/RequestWhiteBalance", this.testId)
        .then((body) => {
          if (this.isNewest(ticket)) this.show(body);
          this.requesting = false;
          this.schedulePoll();
        })
        .catch((err) => {
          const failure = resolveRequestFailure(err);
          if (failure.outcome === "showFreshStatus") {
            // The button stays on "Requesting…" until the status arrives:
            // re-enabled in between, it would offer a request the backend
            // has just refused.
            this.readStatus().then(() => {
              this.requesting = false;
            });
            return;
          }
          this.requesting = false;
          this.requestMessage = failure.message;
          if (failure.outcome === "readAgain") this.readStatus();
        });
    },
  },
};
</script>

<style scoped>
/* The download buttons follow the block directly in the view; the space
   keeps the request button from reading as one of them. */
.white-balance {
  margin-bottom: 16px;
}

/* The app's teal (#00a394) is 3.2:1 on white, below the 4.5:1 needed for
   text, so text and the button use a darker shade of it (5.4:1 on white,
   4.8:1 on the pale teal hover). The app's teal stays for the icon, the
   spinner and the bar. */
.white-balance-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
}

.white-balance-card-failed {
  border-color: #f2c4c4;
}

.white-balance-icon {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e6f6f4;
  color: #00a394;
}

.white-balance-card-failed .white-balance-icon,
.white-balance-notice-failed .white-balance-icon {
  background: #fdecec;
  color: #c52f2f;
}

.white-balance-body {
  flex: 1 1 auto;
  min-width: 0;
}

.white-balance-title {
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  color: #2f3740;
}

.white-balance-muted {
  font-size: 13px;
  line-height: 18px;
  color: #5f6b76;
}

.white-balance-button {
  flex: 0 0 auto;
  padding: 6px 14px;
  background: transparent;
  border: 1px solid #00786d;
  border-radius: 8px;
  color: #00786d;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.white-balance-button:hover:not(:disabled) {
  background: #e6f6f4;
}

.white-balance-button:focus-visible {
  outline: 2px solid #00786d;
  outline-offset: 2px;
}

.white-balance-button:disabled {
  cursor: default;
}

.white-balance-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0;
}

.white-balance-line-icon {
  flex: 0 0 auto;
}

.white-balance-error {
  font-size: 13px;
  line-height: 18px;
  color: #c52f2f;
}

.white-balance-spinner {
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: 2px solid rgba(0, 163, 148, 0.25);
  border-top-color: #00a394;
  border-radius: 50%;
  animation: white-balance-spin 1s linear infinite;
}

@keyframes white-balance-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.white-balance-bar {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 3px;
  overflow: hidden;
  background: #e6f6f4;
}

.white-balance-bar-segment {
  display: block;
  width: 30%;
  height: 100%;
  background: #00a394;
  animation: white-balance-slide 1.6s ease-in-out infinite;
}

/* The segment is 30% of the bar, so -100% starts it just off the left edge
   and 340% takes it just past the right. */
@keyframes white-balance-slide {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(340%);
  }
}

/* Below the dashboard's fixed 60 px top bar (z-index 1000) and under the
   modal overlays (99999), so a dialog the reviewer has open stays on top. */
.white-balance-notice {
  position: fixed;
  top: 72px;
  right: 16px;
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  width: 440px;
  max-width: calc(100% - 32px);
  padding: 10px 8px 10px 12px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(16, 24, 40, 0.12);
  animation: white-balance-notice-in 0.2s ease-out;
}

.white-balance-notice-failed {
  border-color: #f2c4c4;
}

.white-balance-notice .white-balance-icon {
  width: 28px;
  height: 28px;
}

.white-balance-notice .white-balance-title {
  font-size: 14px;
}

.white-balance-notice .white-balance-muted {
  font-size: 12px;
  line-height: 16px;
}

.white-balance-notice .white-balance-button {
  padding: 4px 12px;
  font-size: 13px;
}

.white-balance-notice-close {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: none;
  border: none;
  border-radius: 50%;
  color: #5f6b76;
  cursor: pointer;
}

.white-balance-notice-close:hover {
  background: #f2f4f7;
  color: #2f3740;
}

.white-balance-notice-close:focus-visible {
  outline: 2px solid #00786d;
  outline-offset: 1px;
}

@keyframes white-balance-notice-in {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .white-balance-spinner,
  .white-balance-bar-segment,
  .white-balance-notice {
    animation: none;
  }
}
</style>
